import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const NotFound = () => {
  const location = useLocation();

  // Language detection
  const getLangFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  }, [location.search]);

  const [language, setLanguage] = useState(getLangFromUrl);

  useEffect(() => {
    setLanguage(getLangFromUrl());
  }, [getLangFromUrl]);

  return (
    <div className="dark:bg-dashboard-bg bg-slate-100 h-screen w-full flex flex-col items-center justify-center relative overflow-hidden">
      {/* Main content */}
      <div className="relative z-10 text-center px-4 sm:px-6 max-w-sm sm:max-w-lg md:max-w-2xl mx-auto">
        {/* T-Global Logo */}
        <div className="mb-6 md:mb-8">
          <img
            src="/logo_tglobal.png"
            alt="T-Global"
            className="w-24 sm:w-28 md:w-32 h-auto mx-auto opacity-90"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        {/* 404 Display */}

        {/* Error message */}
        <div className="mb-6 md:mb-8 dark:bg-white/10 bg-black/10 backdrop-blur-md rounded-xl md:rounded-2xl border border-white/20 p-4 sm:p-6 md:p-8 shadow-xl">
          <div className="flex items-center justify-center mb-4 md:mb-6">
            <div className="mb-6 md:mb-8">
              <div className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-orange-500 mb-3 md:mb-4">
                404
              </div>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-orange-500 mx-auto rounded-full"></div>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold  text-gray-500 dark:text-white mb-3 md:mb-4">
            {language === "zh" ? "頁面未找到" : "Page Not Found"}
          </h1>

          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 mb-4 md:mb-6 leading-relaxed px-2">
            {language === "zh"
              ? "抱歉，您要查找的頁面不存在。可能已被移動、删除或您輸入了錯誤的網址。"
              : "Sorry, the page you are looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL."}
          </p>

          {/* Suggestions */}
          <div className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mb-6 md:mb-8">
            <p className="mb-2 md:mb-3">
              {language === "zh" ? "您可以嘗試：" : "You can try:"}
            </p>
            <ul className="text-left max-w-sm md:max-w-md mx-auto space-y-1 md:space-y-2">
              <li>
                •{" "}
                {language === "zh" ? "檢查網址拼寫" : "Check the URL spelling"}
              </li>
              <li>
                • {language === "zh" ? "返回首頁" : "Go back to the homepage"}
              </li>
              <li>
                •{" "}
                {language === "zh" ? "使用導航菜單" : "Use the navigation menu"}
              </li>
            </ul>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
          <Link
            to={`/tg-product-assistant?lang=${language}`}
            className="group flex items-center gap-2 md:gap-3 bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-7 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-orange-500/25 w-full sm:w-auto text-center justify-center"
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5 group-hover:-translate-x-1 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span className="text-sm md:text-base">
              {language === "zh" ? "返回首頁" : "Back to Home"}
            </span>
          </Link>

          <Link
            to={`/tg-product-assistant?lang=${language}`}
            className="group flex items-center gap-2 md:gap-3 bg-black/50 dark:bg-white/10 hover:bg-black/20 hover:dark:bg-white/20 text-white border border-white/20 px-6 sm:px-7 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 backdrop-blur-sm w-full sm:w-auto text-center justify-center"
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5 group-hover:rotate-12 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span className="text-sm md:text-base">
              {language === "zh" ? "AI 對話" : "AI Chat"}
            </span>
          </Link>
        </div>

        {/* Footer text */}
        <div className="mt-12 text-slate-500 text-sm">
          <p>Horuseye PIMS © 2026</p>
          <p className="mt-1">
            {language === "zh"
              ? "產品信息管理系統"
              : "Product Information Management System"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
